import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { LoginService } from '../../Services/login.service';
import { LicenseService } from '../../Services/license.service';
import { Router } from '@angular/router';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import {
  catchError,
  concatMap,
  filter,
  firstValueFrom,
  Subject,
  tap,
  throwError
} from 'rxjs';
import { License } from '../../Model/User/License';
import { SelectionChangedEvent, ColumnButtonClickEvent } from 'devextreme/ui/data_grid';
import { ClickEvent } from 'devextreme/ui/button';
import { ValueChangedEvent as SwitchValueChangedEvent } from 'devextreme/ui/switch';

import { isEqual, cloneDeep } from 'lodash';
import { debounceTime } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddonService } from '../../Services/addon.service';
import { CustomerService } from '../../Services/customer.service';
import { SystemService } from '../../Services/system.service';
import { TranslateService } from '@ngx-translate/core';
import { Message } from '../../Model/System/Message';

const MAX_INT_32 = 2147483647;

@Component({
  selector: 'licenseManagement',
  templateUrl: 'licenseManagement.component.html',
  styleUrls: ['licenseManagement.component.css'],
})
export class LicenseManagementComponent implements OnInit {
  @ViewChild(DxDataGridComponent, { static: false }) dataGrid: DxDataGridComponent;

  private readonly updateLicenseTrigger$ = new Subject<License>();
  private readonly reloadTrigger$ = new Subject<void>();

  public licenseDataSource: DataSource;

  public isAdmin: boolean;
  public isManager: boolean;
  public originalLicense?: License;
  public editingLicense?: License;
  public customerByLicenseDataSource: DataSource;

  private deleteLicenseItem?: License;
  public deleteLicenseConfirmationDialogVisible = false;
  public deleteLicenseConfirmationText: string|undefined;

  public allAddons: string[] = [];
  public licenseTabs: any[] = [];

  constructor(
    private destroyRef: DestroyRef,
    private router: Router,
    private translateService: TranslateService,
    private loginService: LoginService,
    private systemService: SystemService,
    private licenseService: LicenseService,
    private customerService: CustomerService,
    private addonService: AddonService,
  ) {

    this.systemService.currentNavigationTitle = this.translateService.instant('Lizenzen');

    this.licenseTabs = [
      { id: 'restrictions', title: this.translateService.instant('Einschränkungen'), template: 'restrictionsTabTemplate' },
      { id: 'addons', title: this.translateService.instant('Addons'), template: 'addonsTabTemplate' },
      { id: 'customers', title: this.translateService.instant('Zugeordnete Kunden'), template: 'customerTabTemplate' },
    ];

    this.isAdmin = this.loginService.isAdmin;
    this.isManager = this.loginService.isManager;
    this.licenseDataSource = new DataSource(new CustomStore({
      key: 'id',
      loadMode: 'raw',
      load: () => firstValueFrom(this.licenseService.getLicenseList())
    }));

    this.reloadTrigger$
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() =>
        this.licenseDataSource.reload().then(() => {
          if (this.editingLicense != null) {
            this.dataGrid.instance.selectRows([this.editingLicense.id], false);
          }
        })
      );
    
    this.updateLicenseTrigger$.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((editedLicense: License) => this.isDirty(editedLicense)), 
      concatMap((editedLicense: License) => this.licenseService.updateLicense(editedLicense).pipe(
        catchError(error => {
          this.systemService.notify(new Message(this.translateService.instant('Fehler beim Speichern der Lizenz: ') + error.message, 'error'));
          return throwError(() => error);
        }),
        tap(() => {
          this.originalLicense = cloneDeep(editedLicense);
        })
      ))).subscribe(() => {
        this.reloadTrigger$.next();      
      });

    this.addonService.getAllAddons().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(addons => {
      this.allAddons = addons;
    });
  }

  readonly ngOnInit = () => {
    if (this.loginService.currentUser == null) {
      this.router.navigate(['/']);
      return;
    }
  }

  public get unlimitedProducts() {
    return this.editingLicense?.maxProducts === MAX_INT_32;
  }

  public get unlimitedClassifications() {
    return this.editingLicense?.maxClassifications === MAX_INT_32;
  }

  public get unlimitedTemplates() {
    return this.editingLicense?.maxTemplates === MAX_INT_32;
  }

  public get unlimitedOutputChannels() {
    return this.editingLicense?.maxOutputChannels === MAX_INT_32;
  }

  public get unlimitedUsers() {
    return this.editingLicense?.maxUsers === MAX_INT_32;
  }

  readonly unlimitedProductsChanged = (e: SwitchValueChangedEvent) => {

    if (this.editingLicense && e.value && this.editingLicense.maxProducts !== MAX_INT_32) {
      this.editingLicense.maxProducts = MAX_INT_32;
      this.updateLicense();
    } else if (this.editingLicense && !e.value && this.editingLicense.maxProducts === MAX_INT_32) {
      this.editingLicense.maxProducts = 1000;
      this.updateLicense();
    }
  }

  readonly unlimitedClassificationsChanged = (e: SwitchValueChangedEvent) => {

    if (this.editingLicense && e.value && this.editingLicense.maxClassifications !== MAX_INT_32) {
      this.editingLicense.maxClassifications = MAX_INT_32;
      this.updateLicense();
    } else if (this.editingLicense && !e.value && this.editingLicense.maxClassifications === MAX_INT_32) {
      this.editingLicense.maxClassifications = 0;
      this.updateLicense();
    }
  }

  readonly unlimitedTemplatesChanged = (e: SwitchValueChangedEvent) => {

    if (this.editingLicense && e.value && this.editingLicense.maxTemplates !== MAX_INT_32) {
      this.editingLicense.maxTemplates = MAX_INT_32;
      this.updateLicense();
    } else if (this.editingLicense && !e.value && this.editingLicense.maxTemplates === MAX_INT_32) {
      this.editingLicense.maxTemplates = 0;
      this.updateLicense();
    }
  }

  readonly unlimitedOutputChannelsChanged = (e: SwitchValueChangedEvent) => {

    if (this.editingLicense && e.value && this.editingLicense.maxOutputChannels !== MAX_INT_32) {
      this.editingLicense.maxOutputChannels = MAX_INT_32;
      this.updateLicense();
    } else if (this.editingLicense && !e.value && this.editingLicense.maxOutputChannels === MAX_INT_32) {
      this.editingLicense.maxOutputChannels = 0;
      this.updateLicense();
    }
  }
  readonly unlimitedUsersChanged = (e: SwitchValueChangedEvent) => {

    if (this.editingLicense && e.value && this.editingLicense.maxUsers !== MAX_INT_32) {
      this.editingLicense.maxUsers = MAX_INT_32;
      this.updateLicense();
    } else if (this.editingLicense && !e.value && this.editingLicense.maxUsers === MAX_INT_32) {
      this.editingLicense.maxUsers = 1;
      this.updateLicense();
    }
  }

  readonly isDirty = (changedLicense: License) => {
    return !isEqual(this.originalLicense, changedLicense);
  }

  readonly isDeleteConfirmationValid = () => {
    return this.deleteLicenseItem != null && this.deleteLicenseConfirmationText === this.deleteLicenseItem.identifier;
  }

  readonly onGridToolbarPreparing = (e) => {
    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'newLicenseButtonTemplate'
    });
  }

  readonly onLicenseSelected = (e: SelectionChangedEvent) => {

    this.licenseService.getSingleLicense(e.selectedRowsData[0].id)
      .subscribe(license => {
        license.authorizedAddons.sort();
        license.customerDefaultAddons.sort();
        
        this.originalLicense = cloneDeep(license);
        this.editingLicense = cloneDeep(license);
        
        this.customerByLicenseDataSource = new DataSource({
          key: 'id',
          paginate: false,
          store: new CustomStore({
            key: 'id',
            loadMode: 'raw',
            load: () => firstValueFrom(this.customerService.getAllCustomerByLicense(this.editingLicense.id))
          })
        });
      });
  }

  get showToolTips() {
    return this.loginService.showToolTips;
  }

  get showProToolTips() {
    return this.loginService.showProToolTips;
  }

  get showPlaceHolders() {
    return this.loginService.showPlaceHolders;
  }

  readonly onAddLicense = (_: ClickEvent) => {
    this.licenseService.createLicense({ identifier: 'New license' } as License)
      .subscribe((license) => {
        this.originalLicense = cloneDeep(license);
        this.editingLicense = cloneDeep(license);

        this.reloadTrigger$.next();
      });
  }

  readonly updateLicense = () => {
    this.updateLicenseTrigger$.next(this.editingLicense);    
  }

  readonly deleteLicense = (e: ColumnButtonClickEvent) => {
    this.licenseService.getSingleLicense(e.row.data.id)
      .subscribe(license => {
        if (license.customerCount > 0) {
          this.systemService.notify(new Message(this.translateService.instant('Lizenzen können nicht gelöscht werden, wenn sie noch Kunden zugewiesen sind.'), 'info'));
          return;
        }

        this.deleteLicenseItem = license;
        this.deleteLicenseConfirmationText = '';
        this.deleteLicenseConfirmationDialogVisible = true;
      });

  }

  readonly confirmDeleteLicense = () => {
    this.licenseService.deleteLicense(this.deleteLicenseItem.id)
      .subscribe(() => {
        this.deleteLicenseItem = null;
        this.deleteLicenseConfirmationText = '';
        this.deleteLicenseConfirmationDialogVisible = false;
        this.editingLicense = null;

        this.reloadTrigger$.next();
      });
  }
}
